import { useQuery } from '@tanstack/react-query'
import { CornerDownLeft, Search, UserRound, UsersRound, type LucideIcon } from 'lucide-react'
import { Dialog as DialogPrimitive } from 'radix-ui'
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { api } from '@/api/client'
import { Kbd } from '@/components/ui/Kbd'
import { Spinner } from '@/components/ui/Spinner'
import { useAuth } from '@/features/auth/useAuth'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { tAny } from '@/i18n'
import { cn } from '@/utils/cn'
import { flattenNavigation, type NavGroup } from '../navigation'

interface PaletteItem {
  id: string
  label: string
  hint: string
  to: string
  icon?: LucideIcon
}

interface PersonHit {
  id: number
  fullName: string
  studentCode?: string
  employeeCode?: string
}

function score(text: string, query: string): number {
  const t = text.toLowerCase()
  const q = query.toLowerCase().trim()
  if (!q) return 1
  if (t.startsWith(q)) return 3
  if (t.includes(q)) return 2
  // Fuzzy subsequence match ("clsc" → "classes")
  let i = 0
  for (const ch of t) if (ch === q[i]) i++
  return i === q.length ? 1 : 0
}

/**
 * ⌘K / Ctrl+K palette: jump to any permitted page, or find a student / staff member by name or code.
 * Implements the ARIA combobox + listbox pattern with full keyboard navigation.
 */
export function CommandPalette({
  open,
  onOpenChange,
  groups,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  groups: NavGroup[]
}) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { can } = useAuth()
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const listId = useId()
  const listRef = useRef<HTMLUListElement>(null)
  const term = useDebouncedValue(query.trim(), 200)

  const people = useQuery({
    queryKey: ['palette-search', term],
    enabled: open && term.length >= 2 && (can('students.view') || can('staff.view')),
    queryFn: async () => {
      const [students, staff] = await Promise.all([
        can('students.view') ? api.page<PersonHit>('/students', { search: term, perPage: 5 }) : null,
        can('staff.view') ? api.page<PersonHit>('/staff', { search: term, perPage: 5 }) : null,
      ])
      return { students: students?.data ?? [], staff: staff?.data ?? [] }
    },
    staleTime: 30_000,
  })

  const items = useMemo<PaletteItem[]>(() => {
    const pages = flattenNavigation(groups)
      .map((raw) => ({
        ...raw,
        label: tAny(raw.label),
        group: raw.group
          .split(' · ')
          .map((part) => tAny(part))
          .join(' · '),
        source: raw.label,
      }))
      .map((n) => ({
        n,
        s: Math.max(
          score(n.label, query),
          score(n.source, query),
          n.keywords ? score(n.keywords, query) : 0,
          score(n.group, query) > 1 ? 1 : 0,
        ),
      }))
      .filter(({ s }) => s > 0)
      .sort((a, b) => b.s - a.s)
      .slice(0, 8)
      .map(({ n }) => ({
        id: `page:${n.to}:${n.source}`,
        label: n.label,
        hint: n.group,
        to: n.to,
        icon: n.icon,
      }))
    const students = (people.data?.students ?? []).map((s) => ({
      id: `student:${s.id}`,
      label: s.fullName,
      hint: t('Student · {{code}}', { code: s.studentCode ?? '' }),
      to: `/admin/students/${s.id}`,
      icon: UsersRound,
    }))
    const staff = (people.data?.staff ?? []).map((s) => ({
      id: `staff:${s.id}`,
      label: s.fullName,
      hint: t('Staff · {{code}}', { code: s.employeeCode ?? '' }),
      to: `/admin/staff/${s.id}`,
      icon: UserRound,
    }))
    return [...pages, ...students, ...staff]
    // eslint-disable-next-line react-hooks/exhaustive-deps -- labels depend on the active language
  }, [groups, query, people.data, i18n.language, t])

  useEffect(() => {
    listRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' })
  }, [active])

  const go = (item: PaletteItem | undefined) => {
    if (!item) return
    onOpenChange(false)
    void navigate(item.to)
  }

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive((i) => Math.min(items.length - 1, i + 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((i) => Math.max(0, i - 1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      go(items[active])
    }
  }

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 animate-fade-in bg-overlay backdrop-blur-[2px]" />
        <DialogPrimitive.Content className="fixed top-[12vh] left-1/2 z-50 w-[calc(100%-2rem)] max-w-xl -translate-x-1/2 animate-scale-in overflow-hidden rounded-panel border border-line bg-surface shadow-overlay focus:outline-none">
          <DialogPrimitive.Title className="sr-only">{t('Search and jump to')}</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            {t('Type to search pages, students and staff. Use the arrow keys and Enter.')}
          </DialogPrimitive.Description>
          <div className="flex items-center gap-3 border-b border-line px-4">
            <Search className="size-5 text-subtle" aria-hidden="true" />
            <input
              role="combobox"
              aria-expanded="true"
              aria-controls={listId}
              aria-activedescendant={items[active] ? `${listId}-${active}` : undefined}
              aria-autocomplete="list"
              aria-label={t('Search')}
              autoFocus
              value={query}
              onChange={(e) => {
                setQuery(e.target.value)
                setActive(0)
              }}
              onKeyDown={onKeyDown}
              placeholder={t('Search pages, students, staff…')}
              className="h-14 flex-1 bg-transparent text-[0.9375rem] text-ink placeholder:text-subtle focus:outline-none"
            />
            {people.isFetching ? <Spinner className="size-4 text-brand-500" /> : <Kbd>{t('Esc')}</Kbd>}
          </div>
          <ul
            ref={listRef}
            id={listId}
            role="listbox"
            aria-label={t('Results')}
            className="max-h-[min(60vh,26rem)] scrollbar-thin overflow-y-auto p-2"
          >
            {items.length === 0 ? (
              <li className="px-3 py-10 text-center text-sm text-muted">
                {t('No results for “{{query}}”.', { query })}
              </li>
            ) : (
              items.map((item, i) => {
                const Icon = item.icon
                return (
                  // Keyboard interaction is handled by the combobox input (aria-activedescendant).
                  // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                  <li
                    key={item.id}
                    id={`${listId}-${i}`}
                    role="option"
                    aria-selected={i === active}
                    onMouseMove={() => setActive(i)}
                    onClick={() => go(item)}
                    className={cn(
                      'flex cursor-pointer items-center gap-3 rounded-control px-3 py-2.5',
                      i === active ? 'bg-brand-50 text-brand-800' : 'text-ink-soft',
                    )}
                  >
                    <span
                      className={cn(
                        'grid size-8 place-items-center rounded-lg',
                        i === active ? 'bg-white text-brand-600 shadow-xs' : 'bg-surface-sunken text-muted',
                      )}
                    >
                      {Icon ? <Icon className="size-4" aria-hidden="true" /> : null}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">{item.label}</span>
                      <span className="block truncate text-xs text-muted">{item.hint}</span>
                    </span>
                    {i === active ? (
                      <CornerDownLeft className="size-4 text-brand-400" aria-hidden="true" />
                    ) : null}
                  </li>
                )
              })
            )}
          </ul>
          <div className="hidden items-center gap-4 border-t border-line bg-surface-muted px-4 py-2 text-2xs text-muted sm:flex">
            <span className="flex items-center gap-1">
              <Kbd>↑</Kbd>
              <Kbd>↓</Kbd> {t('navigate')}
            </span>
            <span className="flex items-center gap-1">
              <Kbd>↵</Kbd> {t('open')}
            </span>
            <span className="flex items-center gap-1">
              <Kbd>{t('Esc')}</Kbd> {t('close')}
            </span>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
